import React, { forwardRef } from 'react';

import { Loader } from '@/components/Loader';
import { cn } from '@/utils/classname';

type BaseButtonProps = Omit<
  React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  >,
  'size'
>;

export interface ButtonProps extends BaseButtonProps {
  startIcon?: React.ReactNode;
  styleType?:
    | 'primary'
    | 'text'
    | 'none'
    | 'secondary'
    | 'textPrimary'
    | 'header'
    | 'primaryLight'
    | 'rounded'
    | 'error'
    | 'icon';
  endIcon?: React.ReactNode;
  label?: string | React.ReactNode;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  isLoading?: boolean;
  isSelected?: boolean;
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(props, ref) {
    const {
      styleType,
      startIcon,
      label,
      endIcon,
      className,
      children,
      type = 'button',
      isLoading,
      disabled,
      isSelected,
      size = 'md',
      ...restProps
    } = props;

    function getClass() {
      switch (styleType) {
        case 'text':
          return 'text-chariot-text-primary hover:bg-chariot-background-secondary';
        case 'textPrimary':
          return 'text-chariot-brand-primary inline';
        case 'primary':
          return 'bg-chariot-brand-primary text-white shadow-sm hover:bg-chariot-brand-secondary disabled:bg-chariot-stroke disabled:border-chariot-stroke disabled:text-chariot-text-secondary';
        case 'secondary':
          return `bg-chariot-background-secondary text-chariot-text-primary shadow-sm border border-chariot-stroke ${isSelected ? 'bg-chariot-background-secondary' : ''}`;
        case 'header':
          return 'bg-chariot-background-secondary font-medium text-chariot-text-primary shadow-sm';
        case 'primaryLight':
          return 'bg-chariot-brand-primary/10 shadow-sm hover:bg-chariot-brand-primary/20 text-chariot-brand-primary';
        case 'rounded':
          return 'font-medium border border-chariot-brand-primary text-chariot-brand-tertiary hover:bg-chariot-background-secondary hover:text-chariot-text-primary rounded-full disabled:border-transparent';
        case 'none':
          return '';
        case 'error': {
          return 'bg-red-100 hover:bg-red-200 border border-red-500 text-red-800 disabled:border-default';
        }
        case 'icon':
          return 'transition-colors duration-200 items-center rounded-full hover:bg-chariot-brand-primary/10 text-chariot-text-secondary';
        default:
          return 'bg-chariot-background-secondary text-chariot-text-primary';
      }
    }

    function getSizeClass() {
      // Icon-only button detection: has icon but no label/children
      const isIconOnly = (startIcon || endIcon) && !label && !children;

      // Icon-only buttons get minimal padding for proper centering
      if (isIconOnly) {
        return 'p-2';
      }

      switch (size) {
        case 'xxs':
          return 'py-1 px-2 text-xs';
        case 'xs':
          return 'py-1 px-3 text-sm';
        case 'sm':
          return 'py-1.5 px-3 text-sm';
        case 'md':
          return 'py-2 px-4 text-base';
        case 'lg':
          return 'py-2.5 px-5 text-base';
        default:
          return 'py-2 px-4 text-base';
      }
    }

    function getLoadingClass() {
      if (styleType === 'header') {
        return 'bg-chariot-background-secondary';
      }

      return 'bg-chariot-background-secondary';
    }

    return (
      <>
        <button
          {...restProps}
          className={cn(
            'disabled:bg-chariot-stroke disabled:text-chariot-text-secondary flex w-fit cursor-pointer items-center justify-center gap-3 rounded font-bold transition-colors duration-200 disabled:cursor-not-allowed',
            getSizeClass(),
            getClass(),
            className
          )}
          ref={ref}
          type={type}
          disabled={isLoading || disabled}
        >
          {isLoading && (
            <div
              className={cn(
                'absolute top-0 left-0 z-10 size-full rounded-[2px]',
                getLoadingClass()
              )}
            >
              <Loader
                isLoading
                className="size-full rounded-[2px]"
                styleType={styleType === 'header' ? 'header' : undefined}
              />
            </div>
          )}
          {startIcon}
          {label || children}
          {endIcon}
        </button>
      </>
    );
  }
);
